<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">
      Chemical engineering is a branch of engineering which deals with the study of design and
      operation of chemical plants as well as methods of improving production. Chemical engineers
      develop economical commercial processes to convert raw material into useful products.
    </p>
    <p class="mb-5">
      As Chemistry is an area that you will probably not have had a lot to do with so far, we first
      need to set the foundations for what you will do in later activities. So, the first thing you
      need to do is make sure you have the basics sorted.
    </p>
    <h4 class="mb-5">Task 1:</h4>
    <p class="mb-5">
      To find out more about chemical engineering, including about some of the pioneers in the
      field, check out the fascinating video below.
    </p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />
    <!--    <responsive-iframe-youtube :src="videos[1].video" :posterimg="videos[1].image" class="mb-5"/>-->

    <p class="mb-5">Now you’ve watched the video, can you choose the correct answers below?</p>

    <div v-for="(question, index) in questions1" :key="'q1_' + index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <radio-buttons-wrapper
        v-model="data['question_1_' + index]"
        :index="'q_1_' + index"
        :question="question"
        :is-editable="isEditable"
        show-question="false"
      />
    </div>

    <h4 class="mb-5">Task 2: Chemistry Quiz</h4>
    <p class="mb-5">
      While there’s a lot more to being a chemical engineer than just chemistry, having a good
      knowledge of this branch of science is obviously hugely important.
    </p>
    <p class="mb-5">
      Take a look at the questions in the quiz below. How many can you answer? They are mostly
      chemistry-based questions, but there are a few from other branches of science that would be
      useful for chemical engineers to know. In particular, both physics and maths are extremely
      important to chemical engineers.
    </p>
    <p class="mb-5">
      If you don’t know the answer or aren’t sure, take a little time to research online. Keep your
      eye out for interesting facts related to chemistry while doing your research too.
    </p>
    <h4 class="mb-5 text-center">Chemistry Quiz Questions</h4>
    <div v-for="(question, index) in questions2" :key="index">
      <p class="mb-5">{{ question.question }}</p>
      <text-block v-model="data['question_2_' + index]" :editable="isEditable" />
    </div>

    <vs-popup class="holamundo" title="Correct Answers" :active.sync="popupActive">
      <div v-for="(question, index) in questions2" :key="index">
        <h5 class="mb-5">{{ question.question }}</h5>
        <p class="mb-5">
          <strong>Your answer:</strong> <span> {{ data['question_2_' + index] }}</span>
        </p>
        <p class="mb-5"><strong>Correct answer:</strong> {{ question.correct }}</p>
      </div>
    </vs-popup>

    <button class="btn btn-secondary btn-size-medium mb-5" @click="checkAnswers()">
      Submit Quiz and Check Answers
    </button>

    <h4 class="mb-5">Molecules</h4>
    <p class="mb-5">
      Molecular structure is key to understanding the designs of Nature’s mechanisms and blueprints.
      If chemical engineers learn to understand her blueprints and basic materials, then they can
      work to mimic natural products more cost effectively and with less detrimental environmental
      consequences.
    </p>
    <p class="mb-5">Atoms combine and bond to each other to make molecules.</p>
    <p class="mb-5">
      A molecule is made up of a certain number and type of atom. For example, water (H2O) is always
      made from two hydrogen atoms and one oxygen atom. All water molecules have this combination of
      atoms.
    </p>
    <p class="mb-5">
      A molecule has characteristics that are different from the atoms that make it up. For example,
      you couldn’t pour oxygen and hydrogen atoms on a fire and expect to put it out. These atoms
      only act like water when they are bonded together as water molecules.
    </p>
    <p class="mb-5">
      So, what exactly is a molecule then. Watch the video below to find out a little more about
      these mysterious particles.
    </p>
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />
    <!--    <responsive-iframe-youtube :src="videos[2].video" :posterimg="videos[2].image" class="mb-5"/>-->

    <h4 class="mb-5">Task 3:</h4>
    <p class="mb-5">
      Now you’ve some idea about what atoms and molecules are and how they behave, let’s play a fun
      game to practise what we know and to learn some more about this bedrock of chemical
      engineering.
    </p>
    <p class="mb-5">Click the button below to access the game and see how you go.</p>
    <a href="https://testtubegames.com/bondbreaker3.html" target="_blank">
      <centered-image-wrapper
        alt="Play the Bond Breaker game (opens in a new tab)"
        :image="require('@/assets/images/experiences/engineering/bondbreaker_game_button.jpg')"
        classes="2/3"
      />
    </a>
    <p class="mb-5">What level of the game did you get up to? Enter that level below:</p>
    <div class="overflow-hidden mb-5">
      <p class="float-left py-2 pr-2">Level</p>
      <text-block
        v-model="data['question3']"
        :editable="isEditable"
        style="width: 100px; float: left"
        pclass="my-2"
      />
    </div>

    <h4 class="mb-5">Polymers: What Are They and How Are They Made?</h4>
    <centered-image-wrapper
      :image="require('@/assets/images/experiences/engineering/polymers.png')"
      classes="2/3"
      class="w-full mb-0"
      alt="Polymers. Polymers are made by chemical reactions that join lots of small molecules together to make long molecules; for example, poly(ethene) is made by joining thousands of ethene molecules together. Diagram: many separate ethene molecules become one long chain. Polymer properties: chemically unreactive, solids at room temperature, plastic (can be moulded into shape), electrical insulators, strong and hard-wearing. Table of polymers, uses and properties: poly(ethene) or polythene - plastic bags - strong and hard-wearing; PVC - water pipes - strong, hard-wearing, chemically unreactive; PVC - outer layer of electric wires - electrical insulator, hard-wearing; nylon - clothing - can be made into fibres, strong and flexible; Lycra - sports clothing - can be made into fibres, very elastic and tough."
    />
    <p class="text-center mb-5">
      [Source:
      <a href="https://www.bbc.co.uk/bitesize/guides/ztxnsbk/revision/2" target="_blank"
        >https://www.bbc.co.uk/bitesize/guides/ztxnsbk/revision/2]</a
      >
    </p>
    <p class="mb-5">
      Watch the following video to find out a little more about how polymers are developed.
    </p>
    <responsive-youtube-wrapper :video="videos[3].video" :image="videos[3].image" />
    <!--    <responsive-iframe-youtube :src="videos[3].video" :posterimg="videos[3].image" class="mb-5"/>-->
    <p class="mb-5">
      Now, there are no questions to follow this video, you just need to have an understanding of
      polymers to complete the next activity.
    </p>
    <p class="mb-5">
      If you have completed tasks 1-3 you can now click the “Save Activity” button to progress to
      the next activity.
    </p>

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '../../components/ActivitiesBlocks/RadioButtonsWrapper';

export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/chemical_engineering_splash.jpg'),
          video: 'https://www.youtube.com/embed/aRKyJRAxjpM',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/molecule_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/C0Qaf-UJ2XQ',
        },
        3: {
          image: require('@/assets/images/experiences/engineering/polymers_video_splash.jpg'),
          video: 'https://www.youtube.com/embed/rHxxLYzJ8Sw',
        },
      },
      questions1: {
        1: {
          question: '1. Chemical engineering not only focuses on chemicals but also...',
          answers: [
            {
              val: 1,
              answer: 'a. Developing and designing bridges and other infrastructure',
              correct: false,
            },
            { val: 2, answer: 'b. Developing and designing machines and engines', correct: false },
            {
              val: 3,
              answer:
                'c. Developing and designing plants and processes for manufacturing chemicals',
              correct: true,
            },
          ],
        },
        2: {
          question: '2. Biotechnology engineering applies technology to biological systems and...',
          answers: [
            { val: 1, answer: 'a. Chemical processes', correct: false },
            { val: 2, answer: 'b. Living organisms', correct: true },
            { val: 3, answer: 'c. Polymerization', correct: false },
          ],
        },
        3: {
          question: '3. Biomass refers to:',
          answers: [
            { val: 1, answer: 'a. Renewable organic material', correct: true },
            { val: 2, answer: 'b. Plastics and other recyclables', correct: false },
            { val: 3, answer: 'c. Non-renewable resources', correct: false },
          ],
        },
      },
      questions2: {
        1: {
          question: '1. What is the first element on the periodic table?',
          answer: '',
          correct: 'Hydrogen',
        },
        2: {
          question: '2. What is the centre of an atom called?',
          answer: '',
          correct: 'A nucleus',
        },
        3: {
          question: '3. True or false? Acids have a pH level below 7.',
          answer: '',
          correct: 'True',
        },
        4: {
          question: '4. What is the main gas found in the air we breathe?',
          answer: '',
          correct: 'Nitrogen (around 78%)',
        },
        5: {
          question: '5. True or false? An electron carries a positive charge.',
          answer: '',
          correct: 'False',
        },
        6: {
          question:
            '6. Famous New Zealand scientist Ernest Rutherford was awarded a Nobel Prize in which field?',
          answer: '',
          correct: 'Chemistry',
        },
        7: {
          question: '7. What is the chemical symbol for gold?',
          answer: '',
          correct: 'Au',
        },
        8: {
          question: '8. K is the chemical symbol for which element?',
          answer: '',
          correct: 'Potassium',
        },
        9: {
          question: '9. What orbits the nucleus of an atom?',
          answer: '',
          correct: 'Electrons',
        },
        10: {
          question: '10. At room temperature, what is the only metal that is in liquid form?',
          answer: '',
          correct: 'Mercury',
        },
        11: {
          question: '11. True or false? A neutron has no net electric charge.',
          answer: '',
          correct: 'True',
        },
        12: {
          question:
            '12. A nuclear reaction where the nucleus of an atom splits into smaller parts is known as nuclear fission or nuclear fusion?',
          answer: '',
          correct: 'Nuclear fission',
        },
        13: {
          question: '13. What is H20 more commonly known as?',
          answer: '',
          correct: 'Water',
        },
        14: {
          question: '14. What is the third most common gas found in the air we breathe?',
          answer: '',
          correct: 'Argon (around 1%)',
        },
        15: {
          question:
            '15. What is the name given to substances that are initially involved in a chemical reaction?',
          answer: '',
          correct: 'Reactants',
        },
        16: {
          question: '16. True or false? Bases have a pH level below 7.',
          answer: '',
          correct: 'False',
        },
        17: {
          question: '17. Is sodium hydroxide (NaOH) an acid or base?',
          answer: '',
          correct: 'Base',
        },
        18: {
          question:
            '18. Atoms of the same chemical element that have different atomic mass are known as?',
          answer: '',
          correct: 'Isotopes',
        },
        19: {
          question: '19. True or false? A proton carries a positive charge.',
          answer: '',
          correct: 'True',
        },
        20: {
          question:
            '20. What is the fourth most abundant element in the universe in terms of mass?',
          answer: '',
          correct: 'Carbon',
        },
      },
      popupActive: false,
      question3: '',
      uploaded_files: {
        uploaded_file_1: '',
      },
    };
  },
  methods: {
    ...methods,
    checkAnswers() {
      this.popupActive = true;
    },
  },
  computed: {
    ...computed,
    video() {
      return {
        url: 'https://www.youtube.com/embed/WI0pGk0MMhg',
        image: require('@/assets/images/experiences/engineering/instruction_video_trigger.jpg'),
      };
    },
  },
  inject: inject,
  components: { RadioButtonsWrapper },
};
</script>
